import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Plus, Edit, Trash2, Building2, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { payoutCompanyService } from '@/services/payoutCompanyService'
import { PayoutCompany } from '@/types'
import { PayoutCompanyForm, PayoutCompanyFormData, companyToFormData } from './PayoutCompanyForm'
import { ConfirmDialog } from '@/components/common/dialogs/ConfirmDialog'
import { BaseDialog } from '@/components/common/dialogs/BaseDialog'
import { toast } from 'sonner'

export function PayoutCompaniesTab() {
  const { currentUser } = useAuth()
  const [companies, setCompanies] = useState<PayoutCompany[]>([])
  const [loading, setLoading] = useState(true)

  // Dialog state
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<PayoutCompany | null>(null)
  const [deleting, setDeleting] = useState<PayoutCompany | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const load = async () => {
    if (!currentUser) return
    try {
      const list = await payoutCompanyService.getPayoutCompanies(currentUser.uid)
      setCompanies(list)
    } catch (err) {
      console.error('Failed to load payout companies:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [currentUser])

  const handleAdd = async (data: PayoutCompanyFormData) => {
    if (!currentUser) return
    await payoutCompanyService.createPayoutCompany(currentUser.uid, {
      name: data.name,
      organizationNumber: data.organizationNumber || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      postalCode: data.postalCode || undefined,
      phoneNumber: data.phoneNumber || undefined
    })
    toast.success('Bolaget har lagts till')
    setShowAdd(false)
    load()
  }

  const handleEdit = async (data: PayoutCompanyFormData) => {
    if (!currentUser || !editing) return
    await payoutCompanyService.updatePayoutCompany(currentUser.uid, editing.id, {
      name: data.name,
      organizationNumber: data.organizationNumber || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      postalCode: data.postalCode || undefined,
      phoneNumber: data.phoneNumber || undefined
    })
    toast.success('Bolaget har uppdaterats')
    setEditing(null)
    load()
  }

  const handleDelete = async () => {
    if (!currentUser || !deleting) return
    setDeleteLoading(true)
    try {
      await payoutCompanyService.deletePayoutCompany(currentUser.uid, deleting.id)
      toast.success('Bolaget har tagits bort')
      setDeleting(null)
      load()
    } catch (err: any) {
      toast.error(err.message || 'Kunde inte ta bort bolaget')
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Lägg till utbetalande bolag för att tilldela förare till specifika juridiska enheter.
        </p>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Lägg till bolag
        </Button>
      </div>

      {companies.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Inga bolag tillagda ännu</p>
        </div>
      ) : (
        <div className="space-y-2">
          {companies.map(company => (
            <div
              key={company.id}
              className="flex items-center justify-between p-4 border rounded-lg bg-background"
            >
              <div>
                <div className="font-medium">{company.name}</div>
                <div className="text-sm text-muted-foreground space-x-3">
                  {company.organizationNumber && <span>{company.organizationNumber}</span>}
                  {company.address && <span>{company.address}</span>}
                  {company.city && <span>{company.city}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditing(company)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setDeleting(company)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add dialog */}
      <BaseDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        title="Lägg till bolag"
        size="sm"
      >
        <PayoutCompanyForm
          onSubmit={handleAdd}
          onCancel={() => setShowAdd(false)}
          submitLabel="Lägg till"
        />
      </BaseDialog>

      {/* Edit dialog */}
      <BaseDialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        title="Redigera bolag"
        size="sm"
      >
        {editing && (
          <PayoutCompanyForm
            initial={companyToFormData(editing)}
            onSubmit={handleEdit}
            onCancel={() => setEditing(null)}
            submitLabel="Spara"
          />
        )}
      </BaseDialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Ta bort bolag"
        description={`Är du säker på att du vill ta bort "${deleting?.name}"? Förare som är tilldelade detta bolag återgår till standardbolaget.`}
        confirmText="Ta bort"
        cancelText="Avbryt"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleteLoading}
      />
    </div>
  )
}
